<template>
    <div class="container container--commande-list">

        <Loader v-if="!isReady"></Loader>

        <List v-else
            :filtering="this"
            :itemNameSingular="'commande'"
            :itemNamePlural="'commandes'" >

            <template v-slot:actions>
                    
                    <Button 
                        class=".native"
                        :disabled="!hasContent"
                        :loading="processingXlsx"
                        @click.native="downloadXlsx">
                        <span class="fa fa-file-excel-o"></span>
                        Télécharger
                    </Button>
                    
            </template>

            <template v-slot:filters>

                <ListFilter class="filter--main">
                    <input v-model="filters.query"
                        type="text"
                        placeholder="Rechercher un n°, engagement, budget, etc."/>
                </ListFilter>
                <ClientFilter v-model="filters.client_id"
                    :clients="clients"/>
                <SiteFilter v-model="filters.site_id"
                    :clientAddresses="clientAddresses"
                    :markets="markets" />
                <ListFilter class="filter">    
                    <Select v-model="filters.status"
                        :options="status"
                        all="Tous les états">
                    </Select>
                </ListFilter>
                <DateFilter v-model="dates" />

            </template>

            <template v-slot:heading>
                <div class="cell cell--main criteria">
                    <Sorter criteria="numDisplayed">Numéro</Sorter>
                </div>
                <div class="cell criteria">
                    <Sorter criteria="clientRaisonSociale">Client</Sorter>
                </div>
                <div class="cell criteria">
                    <Sorter criteria="engagementNum">Ref. Client</Sorter>
                </div>
                <div class="cell criteria">
                    <Sorter criteria="createdAt">Émise le</Sorter>
                </div>
                <div class="cell cell--state">
                    État
                </div>
                <div class="cell criteria">
                    <Sorter criteria="budgetCode">Budget</Sorter>
                </div>
                <div class="cell cell--price criteria">
                    <Sorter criteria="amountHT">Total HT</Sorter>
                </div>
                <div class="cell cell--price criteria">
                    <Sorter criteria="amountTTC">Total TTC</Sorter>
                </div>  
                <div class="cell cell--actions">
                    Actions
                </div>          
            </template>

            <ul class="list__content commande-list" v-if="hasContent">

                <li class="list__content__item"                
                    v-for="commande in items"
                    :key="commande.id">

                    <ListItem class="list-item--card"
                        :item="commande"
                        :route="route">
                        <div class="cell cell--main">
                            <strong>{{ commande.numDisplayed }}</strong>
                        </div>
                        <div class="cell cell--secondary">{{ commande.clientRaisonSociale }}</div>
                        <div class="cell cell--secondary">{{ commande.engagementNum }}</div>
                        <div class="cell cell--date"><Date :date="commande.createdAt" /></div>
                        <div class="cell cell--state"><CommandeStatus :commande="commande" /></div>
                        <div class="cell cell--secondary">{{ commande.budgetCode }}</div>
                        <div class="cell cell--price"><Price :amount="commande.amountHT" :currency="commande.currency" /></div>
                        <div class="cell cell--price"><Price :amount="commande.amountTTC" :currency="commande.currency" /></div>
                        <div class="cell cell--actions">
                            <button class="woocommerce-button button alt"
                                type="button"
                                v-if="commande.isPurchasable"
                                @click="$event.preventDefault(); purchase(commande)">Commander</button>
                            <button class="woocommerce-button button alt"
                                type="button"
                                v-if="commande.canBePaidByCarteAchatSB2"
                                v-on:click="$event.preventDefault(); pay(commande)">Payer</button>
                        </div>
                    </ListItem>

                </li>

            </ul>

            <div class="container_list container_list--no-result" v-else>
                <h1 class="woocommerce-order-details__title" v-show="!isFiltering">Aucune commande</h1>
            </div>

            <div class="list__next" v-if="hasMore">
                <Loader v-if="isLoadingMore"></Loader>
                <button
                    class="button"
                    type="button"
                    @click="next"
                    v-else>Voir les commandes suivantes</button>
            </div>
            
        </List>

    </div>

</template>

<script>
import { mapState } from 'vuex'

import Button from '@/components/button/Button.vue'
import ClientFilter from '@/components/list/filters/ClientFilter.vue'
import CommandeStatus from '@/components/commandeStatus/CommandeStatus.vue'
import Date from '@/components/date/Date.vue'
import DateFilter from "@/components/list/filters/DateFilter.vue"
import List, { ListFilter, ListItem, Sorter }  from '@/components/list/List.vue'
import Loader from '@/components/loader/Loader.vue'
import Price from '@/components/price/Price.vue'
import Select  from '@/components/select/Select.vue'
import SiteFilter from '@/components/list/filters/SiteFilter.vue'

import api from '@/api/proxyWeb/commandes'
import { routes, goTo } from '@/router'

import filtering from "@/containers/filtering";
import  { useClientAddresses } from '@/helpers/clientAddress'

export default {

    name: 'CommandeList',

    data: function() {
        return {
            filters: {
                client_id: null,
                from: null,
                to: null,
                query: '',
                site_id: null,
                status: null
            },
            route: routes.COMMANDE,
            processingXlsx: false
        }
    },

    computed: {

        status: () => {

            return [
                { value: 'approved', name: 'Approuvée' },
                { value: 'waiting_for_approbation', name: "En attente d'approbation" },
                { value: 'processing', name: 'En cours' },
                { value: 'fully_billed', name: 'Entièrement facturée' },
                { value: 'partially_billed', name: 'Partiellement facturée' },
                { value: 'not_billed', name: 'Non facturée' },
                { value: 'not_paid', name: 'Non payée' },
                { value: 'rejected', name: 'Refusée' },
                { value: 'done', name: 'Terminée' }
            ]      
        },
    
        ...mapState({

            currentClient: state => state.clients.current,

            clients: state => state.clients.all,

            clientAddresses: function(state) {
                return useClientAddresses(state.clients.all, this.filters.client_id)
            },

            markets: state => state.sites.markets
        })
    },

    methods: {
        
        downloadXlsx() {
            this.processingXlsx = true
            api.getXlsx(this.params)
                .then(() => { this.$notify.success('Téléchargement en cours...') })
                .finally(() => { this.processingXlsx = false } )
        }, 
                
        pay: function(commande) {
            goTo( this.$config.SITE_URL + '/wp-content/plugins/myAdelya/tasks/pay/commande/' + commande.id)
        },

        purchase: function(commande) {
            goTo( this.$config.SITE_URL + '/wp-content/plugins/myAdelya/tasks/purchase/commande/' + commande.id )
        },
    },
  
    created () {
        this.useFilter(api)
    },

    components: {
        Button,
        ClientFilter,
        CommandeStatus,
        Date,
        DateFilter,
        List,
        ListFilter,
        ListItem,
        Loader,
        Price,
        Select,
        SiteFilter,
        Sorter
    },

    mixins: [ filtering ]
}
</script>

<style scoped>

.list .list__heading .cell.cell--state,
.list .list__content .cell.cell--state {   
    flex-basis: 290px;
}

</style>
